import { Check, CheckCircle, CalendarBlank, TextAlignLeft, TextAlignJustify, CheckSquare, CaretCircleDown, RadioButton, ListChecks, Signature } from "@phosphor-icons/react";
import ModalCloseButton from "../ui/ModalCloseButton";
import { formatCalendarIsoToDdMmYyyy } from "../../utils/dateUtils";
import "./QuestionnaireViewModal.css";

const FIELD_TYPE_ICONS = {
  short_text: TextAlignLeft,
  long_text: TextAlignJustify,
  date: CalendarBlank,
  checkbox: CheckSquare,
  select: CaretCircleDown,
  radio: RadioButton,
  multi_select: ListChecks,
};

const FIELD_TYPE_LABELS = {
  short_text: "טקסט קצר",
  long_text: "טקסט ארוך",
  date: "תאריך",
  checkbox: "תיבת סימון",
  select: "בחירה מרשימה נפתחת",
  radio: "בחירה מתוך אפשרויות",
  multi_select: "בחירה מרובה",
};

function QuestionnaireViewModal({ submission, onClose }) {
  if (!submission) return null;

  const sortedFields = [...(submission.fields || [])].sort(
    (a, b) => (a.order ?? 0) - (b.order ?? 0)
  );
  const questionCount = sortedFields.filter(
    (f) => f.type !== "section_header"
  ).length;

  // Optional patient signature (only on links that required one; legacy
  // submissions have no `signature` field)
  const signature =
    submission.signature && submission.signature.data && submission.signature.type
      ? submission.signature
      : null;
  const isDrawnSignature =
    signature?.type === "drawn" &&
    typeof signature.data === "string" &&
    signature.data.startsWith("data:image/");
  const signedAtLabel = submission.signedAt
    ? new Date(submission.signedAt).toLocaleDateString("he-IL", {
        day: "numeric",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      })
    : null;

  const formatValue = (field) => {
    if (field.type === "checkbox") {
      return field.value ? "כן" : "לא";
    }
    if (field.type === "date") {
      return field.value ? formatCalendarIsoToDdMmYyyy(field.value) : "-";
    }
    if (field.type === "multi_select") {
      return Array.isArray(field.value) && field.value.length > 0
        ? field.value.join(", ")
        : "-";
    }
    return field.value || "-";
  };

  return (
    <div className="questionnaire-view-overlay" onClick={onClose}>
      <div
        className="questionnaire-view-modal"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="questionnaire-view-header">
          <div className="questionnaire-view-header-info">
            <h2 className="questionnaire-view-title">
              {submission.questionnaireName}
            </h2>
            <div className="questionnaire-view-meta">
              <span className="questionnaire-view-patient">
                {submission.patientName}
              </span>
              {submission.completedAt && (
                <span className="questionnaire-view-date">
                  הושלם:{" "}
                  {new Date(submission.completedAt).toLocaleDateString("he-IL", {
                    day: "numeric",
                    month: "short",
                    year: "numeric",
                    hour: "2-digit",
                    minute: "2-digit",
                  })}
                </span>
              )}
            </div>
          </div>
          <ModalCloseButton onClick={onClose} />
        </div>

        <div className="questionnaire-view-body">
          <div className="questionnaire-view-summary">
            <CheckCircle size={18} weight="fill" />
            <span>{questionCount} שאלות</span>
            {signature && (
              <span className="questionnaire-view-signed-chip">
                <Signature size={13} weight="bold" />
                נחתם דיגיטלית
              </span>
            )}
          </div>

          <div className="questionnaire-view-fields">
            {(() => {
              let questionNumber = 0;
              return sortedFields.map((field) => {
              if (field.type === "section_header") {
                return (
                  <div key={field.id} className="questionnaire-section-header">
                    {field.label}
                  </div>
                );
              }
              questionNumber += 1;
              const index = questionNumber - 1;
              const FieldIcon = FIELD_TYPE_ICONS[field.type] || TextAlignLeft;
              return (
                <div key={field.id} className="questionnaire-field-card">
                  <div className="questionnaire-field-header">
                    <span className="questionnaire-field-number">
                      {index + 1}
                    </span>
                    <div className="questionnaire-field-question">
                      <span className="questionnaire-field-label">
                        {field.label}
                        {field.required && (
                          <span className="questionnaire-field-required">*</span>
                        )}
                      </span>
                      <span className="questionnaire-field-type">
                        <FieldIcon size={12} weight="fill" />
                        {FIELD_TYPE_LABELS[field.type] || field.type}
                      </span>
                    </div>
                  </div>
                  <div
                    className={`questionnaire-field-answer ${
                      field.type === "checkbox" ? "checkbox-answer" : ""
                    } ${field.type === "long_text" ? "long-text-answer" : ""}`}
                  >
                    {field.type === "checkbox" ? (
                      <div className="checkbox-value">
                        <div
                          className={`checkbox-indicator ${
                            field.value ? "checked" : ""
                          }`}
                        >
                          {field.value && <Check size={10} weight="bold" />}
                        </div>
                        <span>{field.value ? "כן" : "לא"}</span>
                      </div>
                    ) : (
                      <span className="field-value-text">
                        {formatValue(field)}
                      </span>
                    )}
                  </div>
                </div>
              );
              });
            })()}
          </div>

          {signature && (
            <div className="questionnaire-signature-card">
              <div className="questionnaire-signature-header">
                <Signature size={16} weight="fill" />
                <span className="questionnaire-signature-title">חתימת המטופל/ת</span>
                {signedAtLabel && (
                  <span className="questionnaire-signature-date">{signedAtLabel}</span>
                )}
              </div>
              <div className="questionnaire-signature-body">
                {isDrawnSignature ? (
                  <img
                    src={signature.data}
                    alt="חתימת המטופל/ת"
                    className="questionnaire-signature-img"
                  />
                ) : (
                  <span className="questionnaire-signature-typed">
                    "{signature.data}"
                  </span>
                )}
              </div>
              <div className="questionnaire-signature-meta">
                {signature.type === "drawn" ? "חתימה ביד" : "שם מוקלד"}
                {submission.signerInfo?.ipAddress &&
                  submission.signerInfo.ipAddress !== "unknown" && (
                    <> · IP {submission.signerInfo.ipAddress}</>
                  )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default QuestionnaireViewModal;
